import { Controller } from "@hotwired/stimulus";
import { animate } from "animejs";
import Plyr from "plyr";
import scrollama from "scrollama";
import throttle from "lodash.throttle";

export default class extends Controller {
  static targets = ["animated", "content", "image", "video"];

  initialize() {
    this.colors = this.data.get("colors").split(",");
    this.href = this.element.href;
    this.player = null;
    this.autoplay = false;
  }

  connect() {
    // Animations
    if (this.element.classList.contains("has-reveal")) {
      this.animateReveal();
    }
    this.element.addEventListener("reveal", this.animateReveal.bind(this));

    if (this.element.classList.contains("has-anim-title")) {
      this.scroller = scrollama();
      this.scroller
        .setup({
          step: [this.element],
        })
        .onStepEnter(this.animateTitle.bind(this));
      this.animatedTargets[0].style.opacity = 0;
    }

    // Closable
    if (this.element.classList.contains("is-closable")) {
      this.element.addEventListener("click", this.close);
    }

    // Video
    this.autoplay = this.element.classList.contains("is-autoplay");

    if (this.hasVideoTarget) {
      this.initVideoPlayer();
    }

    // Horizontal: correct image size
    if (
      this.element.classList.contains("is-horizontal") &&
      this.hasImageTarget &&
      this.hasContentTarget
    ) {
      this.throttledOnResize = throttle(this.onResize.bind(this), 500);
      window.addEventListener("resize", this.throttledOnResize);

      this.onResize();
    }
  }

  disconnect() {
    this.element.removeEventListener("reveal", this.animateReveal.bind(this));

    // Closable
    if (this.element.classList.contains("is-closable")) {
      this.element.removeEventListener("click", this.close);
    }
  }

  onResize() {
    const imageHeight = this.element.offsetHeight - this.contentTarget.offsetHeight;
    this.imageTarget.style.height = `${imageHeight}px`;
  }

  animateReveal() {
    animate(this.animatedTargets, {
      opacity: [0, 1],
      translateX: ["-10rem", "0"],
      duration: 600,
      delay: (el, i) => i * 100,
      ease: "inOutSine",
    });
  }

  animateTitle() {
    if (this.titleAnimated) return;
    this.titleAnimated = true;

    const translateY = window.matchMedia("(max-width: 799px)").matches ? "-0.2rem" : "-0.35rem";

    animate(this.animatedTargets, {
      opacity: [0, 1],
      translateX: ["-10rem", "0"],
      translateY: [translateY, translateY],
      duration: 600,
      delay: (el, i) => i * 100,
      ease: "inOutSine",
    });
  }

  initVideoPlayer() {
    this.player = new Plyr(this.videoTarget, {
      autoplay: this.autoplay,
      clickToPlay: false,
      controls: ["play-large", "play", "progress", "mute", "fullscreen"],
      // debug: true,
      loop: { active: true },
      playsinline: true,
      youtube: { cc_load_policy: 0 },
      vimeo: { texttrack: false },
      // muted: true
    });
  }

  close(e) {
    if (e.target.type === "a" || e.target.type === "button") return;
    window.history.back();
  }

  open(e) {
    if (this.externalLink()) return;
    e.preventDefault();
    this.transitionController.start(this.href, this.colors);
  }

  externalLink() {
    return this.element.getAttribute("target") === "_blank";
  }

  get transitionController() {
    const transitionElement = document.querySelector(".transition");
    return this.application.getControllerForElementAndIdentifier(transitionElement, "transition");
  }
}
